import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Checkbox, Heading, Panel } from '@savgroup-front-common/core';
import { Tag } from '@savgroup-front-common/core/src/atoms/Tag';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';
import { Autocomplete, Input } from '@savgroup-front-common/core/src/atoms/Form';
import { ScanBarcodeIcon } from '@savgroup-front-common/core/src/protons/icons';
import { QrCodeIcon } from '@savgroup-front-common/core/src/protons/icons/QrCode.icon';

import { InventoryMobileQrModal } from '../components/InventoryMobileQr';
import { useInventoryThumbZone } from '../context/InventoryThumbZoneContext';
import {
  type InventoryViewMode,
  useInventoryViewMode,
} from '../context/InventoryViewModeContext';
import { useCurrentUserEmail } from '../hooks/useCurrentUserEmail';
import {
  loadMockLineValidationsForInventory,
  persistMockLineComptageValide,
} from '../utils/inventoryLineValidationStorage';
import {
  appendManualLineToInventory,
  isManualInventoryLineId,
  loadManualLinesForInventory,
  updateManualLineFields,
} from '../utils/inventoryManualPieceStorage';
import {
  loadSupervisorControlsForInventory,
  persistSupervisorControlValide,
} from '../utils/inventorySupervisorControlStorage';
import {
  buildInventoryMobileOpenUrl,
  parseInventoryViewModeFromMobileQrSearch,
} from '../utils/inventoryMobileQr.utils';
import {
  isInventoryClosed,
  persistInventoryClosure,
  POC_INVENTORY_CLOSURE_EVENT,
} from '../utils/inventoryClosureStorage';
import { applyInventoryStockImputation } from '../utils/inventoryStockImputation';

type InventoristeResponsiveEntryMode = 'manuel' | 'scan';

/** Largeur max. (px) pour la vue « responsive » inventaire (résumé, switch Manuel / Scan, etc.). */
const INVENTORY_RESPONSIVE_MAX_WIDTH_PX = 900;

const inventoryResponsiveMediaQuery = `(max-width: ${INVENTORY_RESPONSIVE_MAX_WIDTH_PX}px)`;

interface InventoryLine {
  id: string;
  sku: string;
  referencePiece: string;
  referenceFournisseur: string;
  fournisseur: string;
  article: string;
  emplacement: string;
  quantiteTheorique: number;
  quantiteComptee: string;
  paHt: number;
  pvTtc: number;
  commentaire: string;
  /** Après « Valider », le comptage de la ligne est figé (mode inventoriste). */
  comptageValide: boolean;
  /** Après « Contrôlé », la pièce est validée par le superviseur. */
  controleValide: boolean;
  /** Email de l’inventoriste ayant validé le comptage. */
  comptePar: string;
  /** Email du superviseur ayant validé le contrôle. */
  controlePar: string;
}

interface InventoryHeaderData {
  reference: string;
  site: string;
  stock: string;
  superviseur: string;
  inventoriste: string;
  statut: 'Nouveau' | 'En cours' | 'Clôturé';
}

const $Page = styled.div`
  width: 100%;
`;

const $TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const $TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

/** Export / import : visibles en vue large uniquement (aligné avec la grille inventaire). */
const $TopBarWideOnlyActions = styled.div`
  display: none;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  @media (min-width: 900px) {
    display: flex;
  }
`;

const $SummaryCard = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e7e9f8;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
`;

const $SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 0.65rem 1rem;
  align-items: start;

  @media (min-width: 720px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const $SummaryCell = styled.div`
  min-width: 0;
`;

const $MetaLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const $MetaValue = styled.div`
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  word-break: break-word;
`;

const $SummaryMetaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

/** Inventoriste, vue étroite (≤900px) : switch saisie manuelle / auto sur une ligne. */
const $InventoristeResponsiveBlock = styled.div`
  display: none;
  margin-top: 1.25rem;

  @media (max-width: 900px) {
    display: block;
  }
`;

const $InventoristeEntryModeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  min-width: 0;
`;

const $EntryModeSideLabel = styled.button<{ $isActive: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: none;
  padding: 0.2rem 0;
  font-size: 0.75rem;
  line-height: 1.25;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  color: ${({ $isActive }) => ($isActive ? '#1e3a8a' : '#64748b')};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:first-of-type {
    text-align: right;
  }

  &:last-of-type {
    text-align: left;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const $EntryModeSwitch = styled.button<{ $isScan: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 2.75rem;
  height: 1.5rem;
  border-radius: 999px;
  border: 1px solid ${({ $isScan }) => ($isScan ? '#5369eb' : '#cbd5e1')};
  background: ${({ $isScan }) => ($isScan ? '#5369eb' : '#e2e8f0')};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, border-color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $isScan }) => ($isScan ? 'calc(100% - 1.25rem - 2px)' : '2px')};
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
    transition: left 0.15s ease;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $EntryModeScanAction = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0.2rem 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #5369eb;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const $LineSearchToolbar = styled.div`
  margin-top: 1.25rem;
  width: 100%;
  box-sizing: border-box;
`;

const $LineSearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  max-width: 100%;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
`;

const $LineSearchLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
  flex-shrink: 0;

  @media (min-width: 640px) {
    padding-top: 0.15rem;
  }
`;

const $LineSearchControls = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;

  @media (min-width: 640px) {
    flex: 1;
  }
`;

const $ToolbarIconButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0 0.65rem;
  border: 1px solid #ccd2f9;
  border-radius: 8px;
  background: #f5f7ff;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    border-color: #5a72ff;
    background: #eef1ff;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $ScanBarcodeTrigger = styled($ToolbarIconButton)``;

const $AddPiecePanel = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e7e9f8;
  border-radius: 8px;
  background: #fafbff;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-width: 100%;
  box-sizing: border-box;

  @media (min-width: 720px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.65rem 0.75rem;
  }
`;

const $AddPieceField = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: 720px) {
    min-width: 160px;
    flex: 1 1 160px;
  }
`;

const $AddPieceLabel = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #334155;
`;

const $AddPieceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const $ScanBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.55);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const $ScanPanel = styled.div`
  width: min(100%, 420px);
  max-width: 420px;
  margin: auto;
  flex-shrink: 0;
  border-radius: 12px;
  background: #fff;
  padding: 1rem 1rem 0.85rem;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
`;

const $ScanTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const $ScanVideo = styled.video`
  width: 100%;
  max-height: min(52vh, 360px);
  border-radius: 8px;
  background: #0f172a;
  object-fit: cover;
`;

const $ScanMessage = styled.div<{ $variant?: 'error' | 'info' }>`
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ $variant }) => ($variant === 'error' ? '#b91c1c' : '#475569')};
`;

const $ScanActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
`;

/**
 * Calque « zones pouce » : viewports étroits uniquement (max 719px).
 * Hauteur en dvh + sync visualViewport : évite le décalage où 100vh / inset:0
 * inclut la barre d’adresse (layout viewport « trop haut ») et pousse la zone verte hors écran.
 */
const $ThumbZoneOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  box-sizing: border-box;
  z-index: 50;
  pointer-events: none;
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 0.22s ease, visibility 0.22s ease;

  @media (min-width: 720px) {
    display: none;
  }
`;

const $ThumbLayerRed = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 27%;
  background: rgba(220, 38, 38, 0.14);
`;

const $ThumbLayerOrange = styled.div`
  position: absolute;
  top: 27%;
  left: 0;
  right: 0;
  height: 40%;
  background: rgba(234, 88, 12, 0.14);
`;

const $ThumbLayerGreen = styled.div`
  position: absolute;
  top: 67%;
  left: 0;
  right: 0;
  height: 33%;
  background: rgba(22, 163, 74, 0.14);
`;

const $LineSearchInput = styled.input`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccd2f9;
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #5a72ff;
    box-shadow: 0 0 0 1px rgba(90, 114, 255, 0.2);
  }
`;

const $EmptyFilter = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
`;

const $List = styled.div`
  margin-top: 1rem;
  display: grid;
  gap: 0.75rem;
`;

const $Card = styled.div<{ $isCounted?: boolean }>`
  border: 1px solid
    ${({ theme, $isCounted }) =>
      $isCounted
        ? (theme.newUI?.variationColors?.light4 ?? '#e5e7eb')
        : '#e5e7eb'};
  border-radius: 10px;
  padding: 0.9rem;
  background: ${({ theme, $isCounted }) =>
    $isCounted
      ? (theme.newUI?.variationColors?.light5 ?? theme.colors?.lightGrey ?? '#f5f5f5')
      : (theme.systemColors?.defaultBackground ?? theme.colors?.white ?? '#fff')};
`;

const $LineWorkflowToolbar = styled.div<{ $threeColumnOnResponsive?: boolean }>`
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.65rem;

  ${({ $threeColumnOnResponsive }) =>
    $threeColumnOnResponsive
      ? `
    @media ${inventoryResponsiveMediaQuery} {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 0.4rem;
      width: 100%;
    }
  `
      : ''}
`;

const $WorkflowFilterChip = styled.button<{ $isActive: boolean; $compactOnResponsive?: boolean }>`
  border: 1px solid ${({ $isActive }) => ($isActive ? '#5369eb' : '#ccd2f9')};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 13px;
  line-height: 1.35;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
  cursor: pointer;
  color: ${({ $isActive }) => ($isActive ? '#1e3a8a' : '#374151')};
  background: ${({ $isActive }) => ($isActive ? '#eef1ff' : '#fff')};

  ${({ $compactOnResponsive }) =>
    $compactOnResponsive
      ? `
    @media ${inventoryResponsiveMediaQuery} {
      width: 100%;
      min-width: 0;
      padding: 0.35rem 0.35rem;
      font-size: 11px;
      line-height: 1.25;
      text-align: center;
    }
  `
      : ''}

  &:hover {
    border-color: #5a72ff;
    background: #f5f7ff;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const $CardHeadMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const $CardHeadStatus = styled.div`
  flex-shrink: 0;

  @media ${inventoryResponsiveMediaQuery} {
    display: none;
  }
`;

const $CardHeadTags = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const $SuperviseurLineRow = styled.div`
  margin-top: 0.7rem;
  display: grid;
  grid-template-columns:
    minmax(5.25rem, 1fr)
    minmax(2.5rem, 0.5fr)
    minmax(2.5rem, 0.5fr)
    minmax(2rem, 0.45fr)
    minmax(5.5rem, 1.25fr)
    minmax(4.5rem, auto)
    minmax(4.5rem, auto);
  gap: 0.45rem 0.65rem;
  align-items: stretch;
  width: 100%;
  min-width: 0;

  & > * {
    min-width: 0;
  }

  @media (max-width: 1024px) {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0.2rem;
    gap: 0.75rem;

    & > * {
      flex: 0 0 auto;
    }
  }
`;

const $SuperviseurLineCell = styled.div<{ $minWidth?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: ${({ $minWidth }) => $minWidth ?? '0'};
  max-width: 100%;
  height: 100%;
`;

const $SuperviseurLineCellBody = styled.div<{ $alignStart?: boolean }>`
  display: flex;
  align-items: ${({ $alignStart }) => ($alignStart ? 'flex-start' : 'center')};
  justify-content: flex-start;
  min-height: 44px;
  min-width: 0;
  width: 100%;
  flex: 1;
`;

const INVENTORY_FIELD_FONT_SIZE = '14px';

const $PrixStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  line-height: 1.35;
  color: #111827;
`;

const $SuperviseurValue = styled.div`
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  line-height: 1.35;
  color: #111827;
  width: 100%;
`;

const $SuperviseurCheckboxCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 4.25rem;
  height: 100%;

  ${$SuperviseurLineCellBody} {
    min-height: 44px;
  }
`;

const $SuperviseurCheckboxMeta = styled.span`
  font-size: 0.6875rem;
  line-height: 1.3;
  color: #64748b;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 9.5rem;
`;

const $CardTitle = styled.div`
  font-weight: 600;
  color: #111827;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const $CardSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const $InventoristeLineGrid = styled.div`
  margin-top: 0.7rem;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  align-items: start;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
    column-gap: 1.5rem;
    row-gap: 0.75rem;
    align-items: end;
  }
`;

const $InventoristeQtyCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  width: 100%;
  min-width: 0;
`;

const $InventoristeQtyActionsRow = styled.div<{ $solo?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 0.65rem;

  ${({ $solo }) =>
    $solo
      ? `
    justify-content: flex-start;
  `
      : `
    @media (min-width: 640px) {
      gap: 0.75rem;
    }
  `}
`;

const $InventoristeFieldBody = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  min-width: 0;
  width: 100%;
`;

const $InventoristeValidateWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${inventoryResponsiveMediaQuery} {
    margin-left: auto;
    justify-content: flex-end;
  }
`;

const $QtyStepper = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 0.35rem;
  flex-shrink: 0;
  max-width: 100%;
`;

const $QtyStepButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #5369eb;
  background: #5369eb;
  font-size: 1.25rem;
  line-height: 1;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    border-color: #4458d4;
    background: #4458d4;
  }

  &:active:not(:disabled) {
    border-color: #3d4fc4;
    background: #3d4fc4;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $QtyCountInputWrap = styled.div<{ $inStepper?: boolean }>`
  flex: ${({ $inStepper }) => ($inStepper ? '0 1 3.25rem' : '1')};
  min-width: 2.75rem;
  max-width: ${({ $inStepper }) => ($inStepper ? '3.5rem' : '5.5rem')};
  display: flex;
  align-items: center;

  input {
    width: 100%;
    height: 44px;
    min-height: 44px;
    box-sizing: border-box;
    font-size: ${INVENTORY_FIELD_FONT_SIZE};
    line-height: 1.35;
    text-align: center;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const $InventoryCommentInputWrap = styled.div`
  width: 100%;
  min-width: 0;

  input {
    width: 100%;
    min-height: 44px;
    box-sizing: border-box;
    font-size: ${INVENTORY_FIELD_FONT_SIZE};
    line-height: 1.35;
  }
`;

/** Même structure que la cellule quantité : libellés alignés en tête de colonne (desktop et mobile). */
const $InventoristeCommentCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

const $QtyLockedValue = styled.div`
  min-height: 44px;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  color: #111827;
`;

const $FieldLabel = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: #6b7280;
  margin-bottom: 4px;
  flex-shrink: 0;
  min-height: calc(12px * 1.35);
`;

const $FieldLabelSpacer = styled.div`
  font-size: 12px;
  line-height: 1.35;
  margin-bottom: 4px;
  flex-shrink: 0;
  min-height: calc(12px * 1.35);
  visibility: hidden;
  user-select: none;
`;

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);

const getInventoryHeader = (reference: string): InventoryHeaderData => {
  switch (reference) {
    case 'INV-2026-002':
      return {
        reference,
        site: 'Centre de réparation Aubagne',
        stock: 'Box de Jérome',
        superviseur: 'Thomas Leroy',
        inventoriste: 'Julie Martin',
        statut: 'Clôturé',
      };
    case 'INV-2026-003':
      return {
        reference,
        site: 'Camion Jamel',
        stock: 'Camion Jamel',
        superviseur: 'Sophie Bernard',
        inventoriste: 'Jamel Messaoudi',
        statut: 'En cours',
      };
    case 'INV-2026-004':
      return {
        reference,
        site: 'Camion José',
        stock: 'Camion José',
        superviseur: 'Marc Fontaine',
        inventoriste: 'José Alvarez',
        statut: 'Clôturé',
      };
    case 'INV-2026-005':
      return {
        reference,
        site: 'Auchan Argenteuil',
        stock: 'Box de Lucie',
        superviseur: 'Élise Garnier',
        inventoriste: 'Camille Robert',
        statut: 'Nouveau',
      };
    case 'INV-2026-006':
      return {
        reference,
        site: 'Auchan Neuilly sur marne',
        stock: 'Box de Martin',
        superviseur: 'Claire Dubois',
        inventoriste: 'Nicolas Petit',
        statut: 'En cours',
      };
    case 'INV-2026-001':
    default:
      return {
        reference,
        site: 'Auchan Neuilly sur marne',
        stock: 'Box de Martin',
        superviseur: 'Claire Dubois',
        inventoriste: 'Nicolas Petit',
        statut: 'En cours',
      };
  }
};

const MOCK_FOURNISSEURS = [
  'Eurosav',
  'Eurosav',
  'SAV Parts Pro',
  'Samsung France',
  'LG Service',
  'Philips SAV',
  'APC Distributeur',
  'HP France',
] as const;

const INVENTORY_PIECE_CONSTRUCTEURS = [
  'Nintendo',
  'Apple',
  'Sony',
  'Microsoft',
  'Samsung',
  'Google',
  'LG',
  'Philips',
  'HP',
] as const;

const CONSTRUCTEUR_OPTIONS = INVENTORY_PIECE_CONSTRUCTEURS.map((constructeur) => ({
  value: constructeur,
  label: constructeur,
}));

const getAutocompleteOptionValue = (option: unknown): string => {
  if (!option || typeof option !== 'object' || !('value' in option)) {
    return '';
  }

  const value = (option as { value?: unknown }).value;

  return typeof value === 'string' ? value : '';
};

const getSelectedConstructeurOption = (value: string) =>
  CONSTRUCTEUR_OPTIONS.find((option) => option.value === value);

const MOCK_REFERENCE_PIECES = [
  'NIN-HLL27196',
  'NIN-FMS127857796',
  'NIN-FMS131099915',
  'SAM-GH96-128GB',
  'LG-EDP555-001',
  'PHI-XR200-BAT',
  'APC-BK500-EU',
  'HP-FDM451-INK',
] as const;

const createMockLines = (reference: string): InventoryLine[] =>
  Array.from({ length: 8 }).map((_, index) => ({
    id: `${reference}-L${index + 1}`,
    sku: `SKU-${index + 1001}`,
    referencePiece: MOCK_REFERENCE_PIECES[index] ?? `REF-${index + 9000}`,
    referenceFournisseur: MOCK_REFERENCE_PIECES[index] ?? '',
    fournisseur: MOCK_FOURNISSEURS[index] ?? 'Eurosav',
    article: `Article ${index + 1} - Pièce SAV`,
    emplacement: index < 2 ? '-' : `A${index + 1}-B1`,
    quantiteTheorique: 5 + index * 2,
    quantiteComptee: '',
    paHt: 35 + index * 12.5,
    pvTtc: 59 + index * 21.2,
    commentaire: '',
    comptageValide: false,
    controleValide: false,
    comptePar: '',
    controlePar: '',
  }));

const applyLineCountedQuantityPersist = (
  referenceInventaire: string,
  line: InventoryLine,
  nextComptee: string,
  currentUserEmail: string,
): void => {
  if (isManualInventoryLineId(line.id)) {
    updateManualLineFields(referenceInventaire, line.id, { quantiteComptee: nextComptee });
  } else if (line.comptageValide) {
    persistMockLineComptageValide(
      referenceInventaire,
      line.id,
      nextComptee,
      line.comptePar || currentUserEmail,
    );
  }
};

const updateLineCountedQuantityInState = (
  prev: InventoryLine[],
  lineId: string,
  nextComptee: string,
  asSuperviseur: boolean,
  referenceInventaire: string,
  currentUserEmail: string,
): InventoryLine[] => {
  const item = prev.find((line) => line.id === lineId);

  if (!item) {
    return prev;
  }

  if (isInventoryClosed(referenceInventaire)) {
    return prev;
  }

  if (asSuperviseur) {
    if (item.controleValide) {
      return prev;
    }
  } else if (item.comptageValide) {
    return prev;
  }

  applyLineCountedQuantityPersist(referenceInventaire, item, nextComptee, currentUserEmail);

  return prev.map((line) =>
    line.id === lineId ? { ...line, quantiteComptee: nextComptee } : line,
  );
};

const canSupervisorMarkControle = (line: InventoryLine): boolean =>
  !line.controleValide && (line.comptageValide || line.quantiteComptee.trim() !== '');

type InventoryLineWorkflowFilter = 'all' | 'toCount' | 'toControl' | 'done';

const isLineToCount = (line: InventoryLine): boolean => !line.comptageValide;

const isLineToControl = (line: InventoryLine): boolean =>
  line.comptageValide && !line.controleValide;

const isLineDoneInMode = (line: InventoryLine, inventoryViewMode: InventoryViewMode): boolean =>
  inventoryViewMode === 'superviseur' ? line.controleValide : line.comptageValide;

const getDefaultWorkflowFilter = (inventoryViewMode: InventoryViewMode): InventoryLineWorkflowFilter =>
  inventoryViewMode === 'superviseur' ? 'toControl' : 'toCount';

const getWorkflowFilterLabel = (
  filter: InventoryLineWorkflowFilter,
  inventoryViewMode: InventoryViewMode,
): string => {
  if (filter === 'toCount') {
    return 'À compter';
  }

  if (filter === 'toControl') {
    return 'À contrôler';
  }

  if (filter === 'done') {
    return inventoryViewMode === 'superviseur' ? 'Contrôlées' : 'Comptées';
  }

  return 'Toutes';
};

const sortLinesForWorkflowAll = (
  items: InventoryLine[],
  inventoryViewMode: InventoryViewMode,
): InventoryLine[] =>
  [...items].sort((lineA, lineB) => {
    if (inventoryViewMode === 'superviseur') {
      const rank = (line: InventoryLine): number => {
        if (!line.comptageValide) {
          return 0;
        }

        if (!line.controleValide) {
          return 1;
        }

        return 2;
      };

      return rank(lineA) - rank(lineB);
    }

    if (isLineToCount(lineA) === isLineToCount(lineB)) {
      return 0;
    }

    return isLineToCount(lineA) ? -1 : 1;
  });

const normalizeCountedQuantityInput = (rawValue: string): string | null => {
  const trimmed = rawValue.trim();

  if (trimmed === '') {
    return '';
  }

  const parsed = parseInt(trimmed, 10);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return String(parsed);
};

const resolveInventoryHeader = (referenceInventaire: string): InventoryHeaderData => {
  const base = getInventoryHeader(referenceInventaire);

  if (isInventoryClosed(referenceInventaire)) {
    return { ...base, statut: 'Clôturé' };
  }

  return base;
};

const canCloseInventorySheet = (items: InventoryLine[]): boolean =>
  items.length > 0 && items.every((line) => line.comptageValide && line.controleValide);

const mergeInventoryLinesForReference = (referenceInventaire: string): InventoryLine[] => {
  const mockValidations = loadMockLineValidationsForInventory(referenceInventaire);
  const supervisorControls = loadSupervisorControlsForInventory(referenceInventaire);

  return [
    ...createMockLines(referenceInventaire).map((line) => {
      const validation = mockValidations[line.id];

      const control = supervisorControls[line.id];

      return {
        ...line,
        comptageValide: validation !== undefined,
        quantiteComptee: validation !== undefined ? validation.quantiteComptee : line.quantiteComptee,
        comptePar: validation?.comptePar ?? '',
        controleValide: control !== undefined,
        controlePar: control?.controlePar ?? '',
      };
    }),
    ...loadManualLinesForInventory(referenceInventaire).map((line) => {
      const control = supervisorControls[line.id];

      return {
        ...line,
        comptageValide: line.comptageValide === true,
        comptePar: line.comptePar ?? '',
        controleValide: line.controleValide === true || control !== undefined,
        controlePar: line.controlePar || control?.controlePar || '',
      };
    }),
  ];
};

const getLineCountedQuantity = (quantiteComptee: string): number => {
  const trimmed = quantiteComptee.trim();

  if (trimmed === '') {
    return 0;
  }

  const n = parseInt(trimmed, 10);

  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const hasKnownEmplacement = (emplacement: string): boolean => {
  const t = emplacement.trim();

  return t !== '' && t !== '-';
};

type BarcodeDetectorInstance = {
  detect: (image: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>;
};

type BarcodeDetectorConstructor = new (options?: { formats?: string[] }) => BarcodeDetectorInstance;

const getBarcodeDetectorConstructor = (): BarcodeDetectorConstructor | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
};

const lineMatchesSearch = (line: InventoryLine, query: string): boolean => {
  const q = query.trim().toLowerCase();

  if (!q) {
    return true;
  }

  return (
    line.sku.toLowerCase().includes(q) ||
    line.referencePiece.toLowerCase().includes(q) ||
    line.fournisseur.toLowerCase().includes(q) ||
    line.article.toLowerCase().includes(q) ||
    line.emplacement.toLowerCase().includes(q) ||
    line.id.toLowerCase().includes(q) ||
    line.referenceFournisseur.toLowerCase().includes(q)
  );
};

const findInventoryLineByScannedCode = (
  items: InventoryLine[],
  rawCode: string,
): InventoryLine | undefined => {
  const code = rawCode.trim().toLowerCase();

  if (!code) {
    return undefined;
  }

  return items.find((line) => {
    const candidates = [
      line.sku,
      line.referencePiece,
      line.referenceFournisseur,
      line.id,
    ].map((value) => value.trim().toLowerCase());

    return candidates.some((value) => value !== '' && value === code);
  });
};

const SCAN_READING_DEDUPE_MS = 1200;

const InventoryDetailPage = () => {
  const navigate = useNavigate();
  const { reference = 'INV-2026-001' } = useParams();
  const [searchParams] = useSearchParams();
  const { isThumbZoneOverlayVisible, setThumbZoneOverlayVisible } = useInventoryThumbZone();
  const currentUserEmail = useCurrentUserEmail();
  const { viewMode, setViewMode } = useInventoryViewMode();
  const [lines, setLines] = useState<InventoryLine[]>(() => mergeInventoryLinesForReference(reference));
  const [lineSearchQuery, setLineSearchQuery] = useState('');
  const [lineWorkflowFilter, setLineWorkflowFilter] = useState<InventoryLineWorkflowFilter>(() =>
    getDefaultWorkflowFilter('superviseur'),
  );
  const [inventoryClosureRevision, setInventoryClosureRevision] = useState(0);
  const [isMobileQrModalOpen, setIsMobileQrModalOpen] = useState(false);
  const [isScanCameraOpen, setIsScanCameraOpen] = useState(false);
  const [isAddPieceOpen, setIsAddPieceOpen] = useState(false);
  const [addPieceConstructeur, setAddPieceConstructeur] = useState('');
  const [addPieceRefConstructeur, setAddPieceRefConstructeur] = useState('');
  const [scanCameraError, setScanCameraError] = useState('');
  const [scanCameraInfo, setScanCameraInfo] = useState('');
  const scanVideoRef = useRef<HTMLVideoElement | null>(null);
  const scanRafRef = useRef<number | null>(null);
  const scanBackdropRef = useRef<HTMLDivElement | null>(null);
  const thumbOverlayRef = useRef<HTMLDivElement | null>(null);
  const inventoryImportFileInputRef = useRef<HTMLInputElement | null>(null);
  const inventoryScanBehaviorRef = useRef({
    mode: 'superviseur' as InventoryViewMode,
    inventoristeLayoutEntryMode: 'manuel' as InventoristeResponsiveEntryMode,
    isInventoryResponsiveLayout: false,
  });
  const lastScanDedupeRef = useRef<{ value: string; time: number } | null>(null);

  const [isInventoryResponsiveLayout, setIsInventoryResponsiveLayout] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${INVENTORY_RESPONSIVE_MAX_WIDTH_PX}px)`).matches
      : false,
  );
  const [inventoristeLayoutEntryMode, setInventoristeLayoutEntryMode] =
    useState<InventoristeResponsiveEntryMode>('manuel');
  const onScanCodeDetectedRef = useRef<(rawCode: string) => void>(() => {});

  useEffect(() => {
    setLineSearchQuery('');
    setIsScanCameraOpen(false);
    setIsAddPieceOpen(false);
    setAddPieceConstructeur('');
    setAddPieceRefConstructeur('');
    setThumbZoneOverlayVisible(false);
    setInventoristeLayoutEntryMode('manuel');
    lastScanDedupeRef.current = null;
  }, [reference, setThumbZoneOverlayVisible]);

  useEffect(() => {
    return () => {
      setThumbZoneOverlayVisible(false);
    };
  }, [setThumbZoneOverlayVisible]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mq = window.matchMedia(`(max-width: ${INVENTORY_RESPONSIVE_MAX_WIDTH_PX}px)`);
    const onChange = () => {
      setIsInventoryResponsiveLayout(mq.matches);
    };

    onChange();
    mq.addEventListener('change', onChange);

    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isInventoryResponsiveLayout || viewMode !== 'inventoriste') {
      setInventoristeLayoutEntryMode('manuel');
      lastScanDedupeRef.current = null;
      setIsScanCameraOpen(false);
    }
  }, [isInventoryResponsiveLayout, viewMode]);

  useLayoutEffect(() => {
    if (!isThumbZoneOverlayVisible) {
      return undefined;
    }

    const vv = window.visualViewport;

    if (!vv) {
      return undefined;
    }

    const el = thumbOverlayRef.current;

    if (!el) {
      return undefined;
    }

    const applyVisualViewport = () => {
      el.style.setProperty('height', `${vv.height}px`);
      el.style.setProperty('width', `${vv.width}px`);
      el.style.setProperty('top', `${vv.offsetTop}px`);
      el.style.setProperty('left', `${vv.offsetLeft}px`);
    };

    applyVisualViewport();
    vv.addEventListener('resize', applyVisualViewport);
    vv.addEventListener('scroll', applyVisualViewport);

    return () => {
      vv.removeEventListener('resize', applyVisualViewport);
      vv.removeEventListener('scroll', applyVisualViewport);
      el.style.removeProperty('height');
      el.style.removeProperty('width');
      el.style.removeProperty('top');
      el.style.removeProperty('left');
    };
  }, [isThumbZoneOverlayVisible]);

  useLayoutEffect(() => {
    if (!isScanCameraOpen) {
      return undefined;
    }

    const vv = window.visualViewport;

    if (!vv) {
      return undefined;
    }

    const el = scanBackdropRef.current;

    if (!el) {
      return undefined;
    }

    const applyVisualViewport = () => {
      el.style.setProperty('height', `${vv.height}px`);
      el.style.setProperty('width', `${vv.width}px`);
      el.style.setProperty('top', `${vv.offsetTop}px`);
      el.style.setProperty('left', `${vv.offsetLeft}px`);
    };

    applyVisualViewport();
    vv.addEventListener('resize', applyVisualViewport);
    vv.addEventListener('scroll', applyVisualViewport);

    return () => {
      vv.removeEventListener('resize', applyVisualViewport);
      vv.removeEventListener('scroll', applyVisualViewport);
      el.style.removeProperty('height');
      el.style.removeProperty('width');
      el.style.removeProperty('top');
      el.style.removeProperty('left');
    };
  }, [isScanCameraOpen]);

  useEffect(() => {
    setLines(mergeInventoryLinesForReference(reference));
    setLineWorkflowFilter(getDefaultWorkflowFilter(viewMode));
  }, [viewMode, reference]);

  const header = useMemo(
    () => resolveInventoryHeader(reference),
    [reference, inventoryClosureRevision],
  );

  const isInventorySheetClosed = useMemo(
    () => isInventoryClosed(reference),
    [reference, inventoryClosureRevision],
  );

  const canValidateInventorySheet = useMemo(
    () => canCloseInventorySheet(lines) && !isInventorySheetClosed,
    [isInventorySheetClosed, lines],
  );

  useEffect(() => {
    const onClosureChanged = () => {
      setInventoryClosureRevision((revision) => revision + 1);
    };

    window.addEventListener(POC_INVENTORY_CLOSURE_EVENT, onClosureChanged);

    return () => {
      window.removeEventListener(POC_INVENTORY_CLOSURE_EVENT, onClosureChanged);
    };
  }, []);

  const inventoryMobileOpenUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return buildInventoryMobileOpenUrl('', reference, viewMode);
    }

    return buildInventoryMobileOpenUrl(window.location.origin, reference, viewMode);
  }, [reference, viewMode]);

  useEffect(() => {
    const viewModeFromQr = parseInventoryViewModeFromMobileQrSearch(searchParams);

    if (viewModeFromQr !== undefined) {
      setViewMode(viewModeFromQr);
    }
  }, [searchParams, setViewMode]);

  const toCountLineCount = useMemo(
    () => lines.filter((line) => isLineToCount(line)).length,
    [lines],
  );

  const toControlLineCount = useMemo(
    () => lines.filter((line) => isLineToControl(line)).length,
    [lines],
  );

  const doneLineCount = useMemo(
    () => lines.filter((line) => isLineDoneInMode(line, viewMode)).length,
    [lines, viewMode],
  );

  const filteredLines = useMemo(() => {
    const searchMatches = lines.filter((line) => lineMatchesSearch(line, lineSearchQuery));

    if (lineWorkflowFilter === 'toCount') {
      return searchMatches.filter((line) => isLineToCount(line));
    }

    if (lineWorkflowFilter === 'toControl') {
      return searchMatches.filter((line) => isLineToControl(line));
    }

    if (lineWorkflowFilter === 'done') {
      return searchMatches.filter((line) => isLineDoneInMode(line, viewMode));
    }

    return sortLinesForWorkflowAll(searchMatches, viewMode);
  }, [lineSearchQuery, lineWorkflowFilter, lines, viewMode]);

  const counted = lines.filter((line) => line.comptageValide).length;
  const controlledCount = lines.filter((line) => line.controleValide).length;
  const totalPaHt = lines.reduce((acc, line) => acc + line.paHt, 0);
  const totalPvTtc = lines.reduce((acc, line) => acc + line.pvTtc, 0);

  const handleOpenScanCamera = useCallback(() => {
    setScanCameraError('');
    setScanCameraInfo('');
    setIsScanCameraOpen(true);
  }, []);

  const handleCloseScanCamera = useCallback(() => {
    setIsScanCameraOpen(false);
  }, []);

  const handleSetInventoristeEntryManuel = useCallback(() => {
    setInventoristeLayoutEntryMode('manuel');
    lastScanDedupeRef.current = null;
    setIsScanCameraOpen(false);
  }, []);

  const handleSetInventoristeEntryScan = useCallback(() => {
    setInventoristeLayoutEntryMode('scan');
    setLineSearchQuery('');
    setIsAddPieceOpen(false);
    setScanCameraError('');
    setScanCameraInfo('');
    lastScanDedupeRef.current = null;
    setIsScanCameraOpen(true);
  }, []);

  const handleValiderFicheInventaire = useCallback(() => {
    if (!canCloseInventorySheet(lines)) {
      toast.warn('Toutes les pièces doivent être comptées et contrôlées avant de clôturer l’inventaire.', {
        autoClose: 4000,
        showIcon: true,
      });

      return;
    }

    if (isInventoryClosed(reference)) {
      return;
    }

    const quantiteTheorique = lines.reduce((sum, line) => sum + line.quantiteTheorique, 0);
    const quantiteComptee = lines.reduce(
      (sum, line) => sum + getLineCountedQuantity(line.quantiteComptee),
      0,
    );
    const sommePaHt = lines.reduce((sum, line) => sum + line.paHt, 0);
    const sommePvTtc = lines.reduce((sum, line) => sum + line.pvTtc, 0);

    const closure = persistInventoryClosure({
      reference,
      quantiteTheorique,
      quantiteComptee,
      sommePaHt,
      sommePvTtc,
      nombreArticles: lines.length,
      closedBy: currentUserEmail,
    });

    const imputation = applyInventoryStockImputation(
      reference,
      lines.map((line) => ({
        referencePiece: line.referencePiece,
        quantiteTheorique: line.quantiteTheorique,
        quantiteComptee: getLineCountedQuantity(line.quantiteComptee),
      })),
      closure,
    );

    setInventoryClosureRevision((revision) => revision + 1);

    const ecartLabel =
      imputation.totalEcart > 0
        ? `+${imputation.totalEcart}`
        : String(imputation.totalEcart);

    toast.success(
      imputation.linesWithEcart > 0
        ? `Inventaire clôturé. Stock imputé : ${imputation.linesWithEcart} référence(s), écart total ${ecartLabel} unité(s).`
        : 'Inventaire clôturé. Aucun écart à imputer au stock.',
      { autoClose: 5000, showIcon: true },
    );
  }, [currentUserEmail, lines, reference]);

  const handleExportInventory = useCallback(() => {
    toast.info(
      `Export (POC) — inventaire ${reference} : à brancher sur l’API ou le téléchargement de fichier.`,
      { autoClose: 4000, showIcon: true },
    );
  }, [reference]);

  const handleClickImportInventory = useCallback(() => {
    inventoryImportFileInputRef.current?.click();
  }, []);

  const handleInventoryImportFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) {
      return;
    }

    toast.info(`Import (POC) — fichier « ${file.name} » : traitement à brancher.`, {
      autoClose: 4500,
      showIcon: true,
    });
  }, []);

  const handleSetLineCountedQuantity = useCallback(
    (lineId: string, rawValue: string, asSuperviseur = false) => {
      const nextComptee = normalizeCountedQuantityInput(rawValue);

      if (nextComptee === null) {
        return;
      }

      setLines((prev) =>
        updateLineCountedQuantityInState(
          prev,
          lineId,
          nextComptee,
          asSuperviseur,
          reference,
          currentUserEmail,
        ),
      );
    },
    [currentUserEmail, reference],
  );

  const handleAdjustLineCount = useCallback(
    (lineId: string, delta: number, asSuperviseur = false) => {
      setLines((prev) => {
        const item = prev.find((line) => line.id === lineId);

        if (!item) {
          return prev;
        }

        const nextComptee = String(
          Math.max(0, getLineCountedQuantity(item.quantiteComptee) + delta),
        );

        return updateLineCountedQuantityInState(
          prev,
          lineId,
          nextComptee,
          asSuperviseur,
          reference,
          currentUserEmail,
        );
      });
    },
    [currentUserEmail, reference],
  );

  const handleAddManualPiece = useCallback(() => {
    const constructeur = addPieceConstructeur.trim();
    const refConstructeur = addPieceRefConstructeur.trim();

    if (!constructeur || !refConstructeur) {
      return;
    }

    const id = `${reference}-manual-${Date.now()}`;
    const defaultPaHt = 25;
    const defaultPvTtc = 45;
    const newLine: InventoryLine = {
      id,
      sku: '',
      referencePiece: refConstructeur,
      referenceFournisseur: refConstructeur,
      fournisseur: constructeur,
      article: `Ajout manuel — ${constructeur}`,
      emplacement: '-',
      quantiteTheorique: 1,
      quantiteComptee: '',
      paHt: defaultPaHt,
      pvTtc: defaultPvTtc,
      commentaire: '',
      comptageValide: false,
      controleValide: false,
      comptePar: '',
      controlePar: '',
    };

    appendManualLineToInventory(reference, {
      ...newLine,
      comptageValide: false,
      controleValide: false,
      comptePar: '',
      controlePar: '',
    });
    setLines((prev) => [...prev, newLine]);
    setAddPieceConstructeur('');
    setAddPieceRefConstructeur('');
    setLineSearchQuery('');
  }, [addPieceConstructeur, addPieceRefConstructeur, reference]);

  const handleValiderComptage = useCallback(
    (lineId: string) => {
      setLines((prev) => {
        const item = prev.find((line) => line.id === lineId);

        if (!item || item.comptageValide) {
          return prev;
        }

        const qtyAtValidate = String(getLineCountedQuantity(item.quantiteComptee));

        if (isManualInventoryLineId(lineId)) {
          updateManualLineFields(reference, lineId, {
            comptageValide: true,
            quantiteComptee: qtyAtValidate,
            comptePar: currentUserEmail,
          });
        } else {
          persistMockLineComptageValide(reference, lineId, qtyAtValidate, currentUserEmail);
        }

        return prev.map((line) =>
          line.id === lineId
            ? {
                ...line,
                comptageValide: true,
                quantiteComptee: qtyAtValidate,
                comptePar: currentUserEmail,
              }
            : line,
        );
      });
    },
    [currentUserEmail, reference],
  );

  const handleValiderControleSuperviseur = useCallback(
    (lineId: string) => {
      setLines((prev) => {
        const item = prev.find((line) => line.id === lineId);

        if (!item || item.controleValide || !canSupervisorMarkControle(item)) {
          return prev;
        }

        const qtyAtValidate = String(getLineCountedQuantity(item.quantiteComptee));
        const withComptage = item.comptageValide
          ? item
          : {
              ...item,
              comptageValide: true,
              quantiteComptee: qtyAtValidate,
              comptePar: currentUserEmail,
            };

        if (!item.comptageValide) {
          if (isManualInventoryLineId(lineId)) {
            updateManualLineFields(reference, lineId, {
              comptageValide: true,
              quantiteComptee: qtyAtValidate,
              comptePar: currentUserEmail,
            });
          } else {
            persistMockLineComptageValide(reference, lineId, qtyAtValidate, currentUserEmail);
          }
        }

        if (isManualInventoryLineId(lineId)) {
          updateManualLineFields(reference, lineId, {
            controleValide: true,
            controlePar: currentUserEmail,
          });
        } else {
          persistSupervisorControlValide(reference, lineId, currentUserEmail);
        }

        return prev.map((line) =>
          line.id === lineId
            ? {
                ...withComptage,
                controleValide: true,
                controlePar: currentUserEmail,
              }
            : line,
        );
      });
    },
    [currentUserEmail, reference],
  );

  useEffect(() => {
    if (!isScanCameraOpen) {
      return undefined;
    }

    let isCancelled = false;
    const streamHolder: { current: MediaStream | null } = { current: null };

    const cancelFrame = () => {
      if (scanRafRef.current !== null) {
        cancelAnimationFrame(scanRafRef.current);
        scanRafRef.current = null;
      }
    };

    const releaseCamera = () => {
      cancelFrame();
      if (streamHolder.current) {
        streamHolder.current.getTracks().forEach((track) => track.stop());
        streamHolder.current = null;
      }
      const video = scanVideoRef.current;

      if (video) {
        video.srcObject = null;
      }
    };

    const scheduleDetect = (detector: BarcodeDetectorInstance) => {
      const step = () => {
        if (isCancelled) {
          return;
        }

        const video = scanVideoRef.current;

        if (!video) {
          scanRafRef.current = requestAnimationFrame(step);

          return;
        }

        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          detector
            .detect(video)
            .then((codes) => {
              if (isCancelled) {
                return;
              }

              const raw = codes[0]?.rawValue;

              if (raw) {
                const behavior = inventoryScanBehaviorRef.current;
                const isScanSessionListMode =
                  behavior.mode === 'inventoriste' &&
                  behavior.inventoristeLayoutEntryMode === 'scan' &&
                  behavior.isInventoryResponsiveLayout;

                if (isScanSessionListMode) {
                  const now = Date.now();
                  const last = lastScanDedupeRef.current;

                  if (last && last.value === raw && now - last.time < SCAN_READING_DEDUPE_MS) {
                    scanRafRef.current = requestAnimationFrame(step);

                    return;
                  }

                  lastScanDedupeRef.current = { value: raw, time: now };
                  onScanCodeDetectedRef.current(raw);
                  scanRafRef.current = requestAnimationFrame(step);

                  return;
                }

                lastScanDedupeRef.current = null;
                onScanCodeDetectedRef.current(raw);

                return;
              }

              scanRafRef.current = requestAnimationFrame(step);
            })
            .catch(() => {
              if (!isCancelled) {
                scanRafRef.current = requestAnimationFrame(step);
              }
            });
        } else {
          scanRafRef.current = requestAnimationFrame(step);
        }
      };

      scanRafRef.current = requestAnimationFrame(step);
    };

    const run = async () => {
      setScanCameraError('');
      setScanCameraInfo('');

      if (!navigator.mediaDevices?.getUserMedia) {
        setScanCameraError("La caméra n'est pas disponible dans ce navigateur.");

        return;
      }

      let stream: MediaStream;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch {
        setScanCameraError(
          "Impossible d'activer la caméra. Vérifiez les permissions ou utilisez une connexion sécurisée (HTTPS).",
        );

        return;
      }

      if (isCancelled) {
        stream.getTracks().forEach((track) => track.stop());

        return;
      }

      streamHolder.current = stream;
      const video = scanVideoRef.current;

      if (video) {
        video.setAttribute('playsinline', 'true');
        video.playsInline = true;
        video.muted = true;
        video.srcObject = stream;
        try {
          await video.play();
        } catch {
          // lecture vidéo peut échouer selon le navigateur
        }
      }

      if (isCancelled) {
        releaseCamera();

        return;
      }

      const BarcodeDetectorCtor = getBarcodeDetectorConstructor();

      if (!BarcodeDetectorCtor) {
        setScanCameraInfo(
          'La lecture automatique du code-barres nécessite un navigateur compatible (ex. Chrome sur Android). ' +
            'La caméra est active : cadrez le code ou saisissez-le à la main dans le champ recherche.',
        );

        return;
      }

      try {
        const detector = new BarcodeDetectorCtor({
          formats: [
            'aztec',
            'code_128',
            'code_39',
            'code_93',
            'codabar',
            'data_matrix',
            'ean_13',
            'ean_8',
            'itf',
            'pdf417',
            'qr_code',
            'upc_a',
            'upc_e',
          ],
        });

        scheduleDetect(detector);
      } catch {
        setScanCameraInfo(
          "Impossible d'initialiser la détection des codes-barres. La caméra reste ouverte pour aide visuelle.",
        );
      }
    };

    void run();

    return () => {
      isCancelled = true;
      releaseCamera();
    };
  }, [isScanCameraOpen]);

  useEffect(() => {
    if (!isScanCameraOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseScanCamera();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isScanCameraOpen, handleCloseScanCamera]);

  inventoryScanBehaviorRef.current = {
    mode: viewMode,
    inventoristeLayoutEntryMode,
    isInventoryResponsiveLayout,
  };

  onScanCodeDetectedRef.current = (rawCode: string) => {
    const code = rawCode.trim();

    if (!code) {
      return;
    }

    const behavior = inventoryScanBehaviorRef.current;
    const isResponsiveScanIncrement =
      behavior.mode === 'inventoriste' &&
      behavior.inventoristeLayoutEntryMode === 'scan' &&
      behavior.isInventoryResponsiveLayout;

    if (isResponsiveScanIncrement) {
      setLines((prev) => {
        const line = findInventoryLineByScannedCode(prev, code);

        if (!line) {
          toast.warn(`Aucune pièce trouvée pour « ${code} ».`, {
            autoClose: 3500,
            showIcon: true,
          });

          return prev;
        }

        if (line.comptageValide) {
          toast.info(`« ${line.referencePiece} » est déjà validée.`, {
            autoClose: 3500,
            showIcon: true,
          });

          return prev;
        }

        const nextComptee = String(getLineCountedQuantity(line.quantiteComptee) + 1);

        toast.success(`+1 · ${line.referencePiece} (qté ${nextComptee})`, {
          autoClose: 2200,
          showIcon: true,
        });

        return updateLineCountedQuantityInState(
          prev,
          line.id,
          nextComptee,
          false,
          reference,
          currentUserEmail,
        );
      });

      return;
    }

    setLineSearchQuery(code);
    setIsScanCameraOpen(false);
  };

  const showInventoristeResponsiveEntry =
    viewMode === 'inventoriste' && isInventoryResponsiveLayout;

  const showInventoristeLineSearch =
    viewMode !== 'inventoriste' ||
    !isInventoryResponsiveLayout ||
    inventoristeLayoutEntryMode === 'manuel';

  const showInventoristeToolbarScanButton =
    viewMode === 'inventoriste' && !isInventoryResponsiveLayout;

  const isResponsiveScanIncrementActive =
    showInventoristeResponsiveEntry && inventoristeLayoutEntryMode === 'scan';

  return (
    <$Page>
      <Panel>
        <$TopBar>
          <Heading level={2}>Inventaire {header.reference}</Heading>
          <$TopBarRight>
            <$TopBarWideOnlyActions>
              <input
                ref={inventoryImportFileInputRef}
                type="file"
                accept={
                  '.csv,.xlsx,.xls,text/csv,' +
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                }
                style={{ display: 'none' }}
                aria-hidden
                tabIndex={-1}
                onChange={handleInventoryImportFileChange}
              />
              <$ToolbarIconButton
                type="button"
                onClick={() => setIsMobileQrModalOpen(true)}
                aria-label="Afficher le QR code pour ouvrir sur mobile"
                title="Ouvrir sur mobile (QR code)"
              >
                <QrCodeIcon size="22px" color="#334155" />
              </$ToolbarIconButton>
              <Button type={BUTTON_TYPES.BUTTON} secondary onClick={handleExportInventory}>
                Exporter
              </Button>
              <Button type={BUTTON_TYPES.BUTTON} secondary onClick={handleClickImportInventory}>
                Importer
              </Button>
            </$TopBarWideOnlyActions>
            <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={() => navigate('/inventaire')}>
              Retour
            </Button>
            {viewMode === 'superviseur' && !isInventorySheetClosed ? (
              <Button
                type={BUTTON_TYPES.BUTTON}
                primary
                onClick={handleValiderFicheInventaire}
                disabled={!canValidateInventorySheet}
                title={
                  canValidateInventorySheet
                    ? 'Clôturer l’inventaire et imputer les écarts au stock'
                    : 'Comptez et contrôlez toutes les pièces avant de clôturer'
                }
              >
                Clôturer l'inventaire
              </Button>
            ) : null}
          </$TopBarRight>
        </$TopBar>

        <$SummaryCard>
          <$SummaryGrid>
            <$SummaryCell>
              <$MetaLabel>Stock</$MetaLabel>
              <$SummaryMetaStack>
                <$MetaValue>{header.stock}</$MetaValue>
                <$MetaValue>{header.site}</$MetaValue>
              </$SummaryMetaStack>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Statut</$MetaLabel>
              <div style={{ marginTop: 2 }}>
                <Tag
                  alert={header.statut === 'En cours'}
                  success={header.statut === 'Clôturé'}
                  primary={header.statut === 'Nouveau'}
                  withoutMargin
                  isSmall
                >
                  {header.statut}
                </Tag>
              </div>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Superviseur</$MetaLabel>
              <$MetaValue>{header.superviseur}</$MetaValue>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Inventoriste</$MetaLabel>
              <$MetaValue>{header.inventoriste}</$MetaValue>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Progression</$MetaLabel>
              {viewMode === 'superviseur' ? (
                <$SummaryMetaStack>
                  <$MetaValue>
                    {counted} / {lines.length} pièces comptées
                  </$MetaValue>
                  <$MetaValue>
                    {controlledCount} / {lines.length} pièces contrôlées
                  </$MetaValue>
                </$SummaryMetaStack>
              ) : (
                <$MetaValue>
                  {counted} / {lines.length} articles comptés
                </$MetaValue>
              )}
            </$SummaryCell>
            {viewMode === 'superviseur' ? (
              <$SummaryCell>
                <$MetaLabel>Totaux</$MetaLabel>
                <$SummaryMetaStack>
                  <$MetaValue>PA HT {formatAmount(totalPaHt)}</$MetaValue>
                  <$MetaValue>PV TTC {formatAmount(totalPvTtc)}</$MetaValue>
                </$SummaryMetaStack>
              </$SummaryCell>
            ) : null}
          </$SummaryGrid>
        </$SummaryCard>

        {showInventoristeResponsiveEntry ? (
          <$InventoristeResponsiveBlock>
            <$InventoristeEntryModeRow>
              <$EntryModeSideLabel
                type="button"
                $isActive={inventoristeLayoutEntryMode === 'manuel'}
                onClick={handleSetInventoristeEntryManuel}
                title="Rechercher une pièce et l’ajouter"
              >
                Saisie manuelle
              </$EntryModeSideLabel>
              <$EntryModeSwitch
                type="button"
                role="switch"
                $isScan={inventoristeLayoutEntryMode === 'scan'}
                aria-checked={inventoristeLayoutEntryMode === 'scan'}
                aria-label={
                  inventoristeLayoutEntryMode === 'scan'
                    ? 'Passer en saisie manuelle'
                    : 'Passer en saisie automatique (scan)'
                }
                onClick={() => {
                  if (inventoristeLayoutEntryMode === 'scan') {
                    handleSetInventoristeEntryManuel();
                  } else {
                    handleSetInventoristeEntryScan();
                  }
                }}
              />
              <$EntryModeSideLabel
                type="button"
                $isActive={inventoristeLayoutEntryMode === 'scan'}
                onClick={handleSetInventoristeEntryScan}
                title="Scanner : chaque code ajoute +1 sur la pièce"
              >
                Saisie auto
              </$EntryModeSideLabel>
              {inventoristeLayoutEntryMode === 'scan' && !isScanCameraOpen ? (
                <$EntryModeScanAction
                  type="button"
                  onClick={handleSetInventoristeEntryScan}
                >
                  Ouvrir
                </$EntryModeScanAction>
              ) : null}
            </$InventoristeEntryModeRow>
          </$InventoristeResponsiveBlock>
        ) : null}

        <$LineSearchToolbar>
          {showInventoristeLineSearch ? (
            <>
              <$LineSearchRow>
                <$LineSearchLabel htmlFor="inventory-line-search">Rechercher une pièce</$LineSearchLabel>
                <$LineSearchControls>
                  <$LineSearchInput
                    id="inventory-line-search"
                    type="search"
                    name="inventory-line-search"
                    value={lineSearchQuery}
                    onChange={(event) => setLineSearchQuery(event.target.value)}
                    placeholder="Réf. pièce, fournisseur, libellé ou SKU…"
                    autoComplete="off"
                    spellCheck={false}
                    enterKeyHint="search"
                    inputMode="search"
                  />
                  {viewMode === 'superviseur' || showInventoristeToolbarScanButton ? (
                    <$ScanBarcodeTrigger
                      type="button"
                      onClick={handleOpenScanCamera}
                      aria-label="Scanner un code-barres avec la caméra"
                      title="Scanner un code-barres"
                    >
                      <ScanBarcodeIcon size="22px" color="#334155" />
                    </$ScanBarcodeTrigger>
                  ) : null}
                  <Button
                    type={BUTTON_TYPES.BUTTON}
                    secondary
                    onClick={() => setIsAddPieceOpen((open) => !open)}
                    aria-expanded={isAddPieceOpen}
                    aria-label={
                      isAddPieceOpen
                        ? 'Fermer le formulaire d’ajout de pièce'
                        : 'Ouvrir le formulaire pour ajouter une pièce à l’inventaire'
                    }
                    title={isAddPieceOpen ? 'Fermer l’ajout de pièce' : 'Ajouter une pièce'}
                  >
                    Ajouter
                  </Button>
                </$LineSearchControls>
              </$LineSearchRow>
              {isAddPieceOpen ? (
                <$AddPiecePanel>
                  <$AddPieceField>
                    <$AddPieceLabel htmlFor="add-piece-constructeur">Constructeur</$AddPieceLabel>
                    <Autocomplete
                      name="add-piece-constructeur"
                      options={CONSTRUCTEUR_OPTIONS}
                      value={getSelectedConstructeurOption(addPieceConstructeur)}
                      placeholder="Sélectionner un constructeur"
                      onChange={(option) => {
                        setAddPieceConstructeur(getAutocompleteOptionValue(option));
                      }}
                    />
                  </$AddPieceField>
                  <$AddPieceField>
                    <$AddPieceLabel htmlFor="add-piece-ref-constructeur">Réf. constructeur</$AddPieceLabel>
                    <Input
                      name="add-piece-ref-constructeur"
                      value={addPieceRefConstructeur}
                      placeholder="Référence constructeur"
                      onChange={(event) => setAddPieceRefConstructeur(event.target.value)}
                    />
                  </$AddPieceField>
                  <$AddPieceActions>
                    <Button
                      type={BUTTON_TYPES.BUTTON}
                      secondary
                      onClick={handleAddManualPiece}
                      disabled={!addPieceConstructeur.trim() || !addPieceRefConstructeur.trim()}
                    >
                      Ajouter
                    </Button>
                  </$AddPieceActions>
                </$AddPiecePanel>
              ) : null}
            </>
          ) : null}
          <$LineWorkflowToolbar $threeColumnOnResponsive={showInventoristeResponsiveEntry}>
            <$WorkflowFilterChip
              type="button"
              $isActive={lineWorkflowFilter === 'toCount'}
              $compactOnResponsive={showInventoristeResponsiveEntry}
              onClick={() => setLineWorkflowFilter('toCount')}
            >
              À compter ({toCountLineCount})
            </$WorkflowFilterChip>
            {viewMode === 'superviseur' ? (
              <$WorkflowFilterChip
                type="button"
                $isActive={lineWorkflowFilter === 'toControl'}
                onClick={() => setLineWorkflowFilter('toControl')}
              >
                À contrôler ({toControlLineCount})
              </$WorkflowFilterChip>
            ) : null}
            <$WorkflowFilterChip
              type="button"
              $isActive={lineWorkflowFilter === 'done'}
              $compactOnResponsive={showInventoristeResponsiveEntry}
              onClick={() => setLineWorkflowFilter('done')}
            >
              {getWorkflowFilterLabel('done', viewMode)} ({doneLineCount})
            </$WorkflowFilterChip>
            <$WorkflowFilterChip
              type="button"
              $isActive={lineWorkflowFilter === 'all'}
              $compactOnResponsive={showInventoristeResponsiveEntry}
              onClick={() => setLineWorkflowFilter('all')}
            >
              Toutes ({lines.length})
            </$WorkflowFilterChip>
          </$LineWorkflowToolbar>
        </$LineSearchToolbar>

        <$List>
          {filteredLines.length === 0 ? (
            <$EmptyFilter>
              {lineSearchQuery.trim() !== ''
                ? `Aucune pièce ne correspond à « ${lineSearchQuery.trim()} » avec le filtre « ${getWorkflowFilterLabel(lineWorkflowFilter, viewMode)} ».`
                : lineWorkflowFilter === 'toCount'
                  ? 'Aucune pièce restante à compter.'
                  : lineWorkflowFilter === 'toControl'
                    ? 'Aucune pièce restante à contrôler (comptage validé requis).'
                    : lineWorkflowFilter === 'done'
                      ? 'Aucune pièce terminée pour ce filtre.'
                      : 'Aucune pièce dans cet inventaire.'}
            </$EmptyFilter>
          ) : (
            filteredLines.map((line) => (
              <$Card key={line.id} $isCounted={line.comptageValide}>
                <$CardHead>
                  <$CardHeadMain>
                    <$CardTitle>
                      {line.referencePiece} - {line.fournisseur}
                    </$CardTitle>
                    {hasKnownEmplacement(line.emplacement) ? (
                      <$CardSub>{line.emplacement}</$CardSub>
                    ) : null}
                  </$CardHeadMain>
                  <$CardHeadStatus>
                    <$CardHeadTags>
                      {line.controleValide ? (
                        <Tag primary withoutMargin isSmall>
                          Contrôlé
                        </Tag>
                      ) : line.comptageValide ? (
                        <Tag success withoutMargin isSmall>
                          Compté
                        </Tag>
                      ) : (
                        <Tag alert withoutMargin isSmall>
                          A compter
                        </Tag>
                      )}
                    </$CardHeadTags>
                  </$CardHeadStatus>
                </$CardHead>

                {viewMode === 'superviseur' ? (
                  <$SuperviseurLineRow>
                    <$SuperviseurLineCell $minWidth="5.25rem">
                      <$FieldLabel>PA HT / PV TTC</$FieldLabel>
                      <$SuperviseurLineCellBody $alignStart>
                        <$PrixStack>
                          <span>{formatAmount(line.paHt)}</span>
                          <span>{formatAmount(line.pvTtc)}</span>
                        </$PrixStack>
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="2.5rem">
                      <$FieldLabel>Qté théorique</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <$SuperviseurValue>{line.quantiteTheorique}</$SuperviseurValue>
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="8.5rem">
                      <$FieldLabel>Qté comptée</$FieldLabel>
                      <$SuperviseurLineCellBody>
                      {line.controleValide ? (
                        <$SuperviseurValue>{line.quantiteComptee || '—'}</$SuperviseurValue>
                      ) : (
                        <$QtyStepper>
                          <$QtyStepButton
                            type="button"
                            aria-label="Diminuer la quantité comptée"
                            disabled={getLineCountedQuantity(line.quantiteComptee) <= 0}
                            onClick={() => handleAdjustLineCount(line.id, -1, true)}
                          >
                            -
                          </$QtyStepButton>
                          <$QtyCountInputWrap $inStepper>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              name={`superviseur-qty-${line.id}`}
                              value={line.quantiteComptee}
                              onChange={(event) => {
                                handleSetLineCountedQuantity(line.id, event.target.value, true);
                              }}
                              aria-label="Quantité comptée"
                            />
                          </$QtyCountInputWrap>
                          <$QtyStepButton
                            type="button"
                            aria-label="Augmenter la quantité comptée"
                            onClick={() => handleAdjustLineCount(line.id, 1, true)}
                          >
                            +
                          </$QtyStepButton>
                        </$QtyStepper>
                      )}
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="2rem">
                      <$FieldLabel>Écart</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <$SuperviseurValue>
                          {line.quantiteComptee
                            ? Number(line.quantiteComptee) - line.quantiteTheorique
                            : '—'}
                        </$SuperviseurValue>
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="6rem">
                      <$FieldLabel>Commentaire</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <$SuperviseurValue title={line.commentaire || undefined}>
                          {line.commentaire || '—'}
                        </$SuperviseurValue>
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurCheckboxCell>
                      <$FieldLabelSpacer>&nbsp;</$FieldLabelSpacer>
                      <$SuperviseurLineCellBody>
                      <Checkbox
                        name={`superviseur-comptee-${line.id}`}
                        label="Comptée"
                        checked={line.comptageValide}
                        disabled={line.comptageValide || line.controleValide}
                        withoutMargin
                        dataTestId={`inventory-line-comptee-${line.id}`}
                        title={line.comptePar || undefined}
                        ariaLabel={
                          line.comptePar
                            ? `Comptée par ${line.comptePar}`
                            : 'Valider le comptage de la pièce'
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            handleValiderComptage(line.id);
                          }
                        }}
                      />
                      </$SuperviseurLineCellBody>
                      {line.comptePar ? (
                        <$SuperviseurCheckboxMeta>{line.comptePar}</$SuperviseurCheckboxMeta>
                      ) : null}
                    </$SuperviseurCheckboxCell>
                    <$SuperviseurCheckboxCell>
                      <$FieldLabelSpacer>&nbsp;</$FieldLabelSpacer>
                      <$SuperviseurLineCellBody>
                      <Checkbox
                        name={`superviseur-controlee-${line.id}`}
                        label="Contrôlée"
                        checked={line.controleValide}
                        disabled={!canSupervisorMarkControle(line)}
                        withoutMargin
                        dataTestId={`inventory-line-controlee-${line.id}`}
                        title={line.controlePar || undefined}
                        ariaLabel={
                          line.controlePar
                            ? `Contrôlée par ${line.controlePar}`
                            : 'Marquer la pièce comme contrôlée'
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            handleValiderControleSuperviseur(line.id);
                          }
                        }}
                      />
                      </$SuperviseurLineCellBody>
                      {line.controlePar ? (
                        <$SuperviseurCheckboxMeta>{line.controlePar}</$SuperviseurCheckboxMeta>
                      ) : null}
                    </$SuperviseurCheckboxCell>
                  </$SuperviseurLineRow>
                ) : (
                  <$InventoristeLineGrid>
                    <$InventoristeQtyCell>
                      <$FieldLabel>Quantité comptée</$FieldLabel>
                      <$InventoristeFieldBody>
                      {!line.comptageValide ? (
                        <$InventoristeQtyActionsRow>
                          <$QtyStepper>
                            <$QtyStepButton
                              type="button"
                              aria-label="Diminuer la quantité comptée"
                              disabled={getLineCountedQuantity(line.quantiteComptee) <= 0}
                              onClick={() => handleAdjustLineCount(line.id, -1)}
                            >
                              -
                            </$QtyStepButton>
                            <$QtyCountInputWrap $inStepper>
                              <Input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                name={`inventoriste-qty-${line.id}`}
                                value={line.quantiteComptee}
                                onChange={(event) => {
                                  handleSetLineCountedQuantity(line.id, event.target.value);
                                }}
                                aria-label="Quantité comptée"
                              />
                            </$QtyCountInputWrap>
                            <$QtyStepButton
                              type="button"
                              aria-label="Augmenter la quantité comptée"
                              onClick={() => handleAdjustLineCount(line.id, 1)}
                            >
                              +
                            </$QtyStepButton>
                          </$QtyStepper>
                          <$InventoristeValidateWrap>
                            <Button
                              type={BUTTON_TYPES.BUTTON}
                              primary
                              onClick={() => handleValiderComptage(line.id)}
                            >
                              Valider
                            </Button>
                          </$InventoristeValidateWrap>
                        </$InventoristeQtyActionsRow>
                      ) : (
                        <$InventoristeQtyActionsRow $solo>
                          <$QtyLockedValue aria-live="polite">
                            {line.quantiteComptee || '0'}
                          </$QtyLockedValue>
                        </$InventoristeQtyActionsRow>
                      )}
                      </$InventoristeFieldBody>
                    </$InventoristeQtyCell>
                    <$InventoristeCommentCell>
                      <$FieldLabel>Commentaire</$FieldLabel>
                      <$InventoristeFieldBody>
                      <$InventoryCommentInputWrap>
                        <Input
                          name={`comment-${line.id}`}
                          value={line.commentaire}
                          placeholder="Optionnel"
                          isDisabled={line.comptageValide}
                          onChange={(event) => {
                          const value = event.target.value;

                          setLines((prev) =>
                            prev.map((item) =>
                              item.id === line.id ? { ...item, commentaire: value } : item,
                            ),
                          );

                          if (isManualInventoryLineId(line.id)) {
                            updateManualLineFields(reference, line.id, { commentaire: value });
                          }
                        }}
                        />
                      </$InventoryCommentInputWrap>
                      </$InventoristeFieldBody>
                    </$InventoristeCommentCell>
                  </$InventoristeLineGrid>
                )}
              </$Card>
            ))
          )}
        </$List>
      </Panel>
      {typeof document !== 'undefined' &&
        createPortal(
          <$ThumbZoneOverlay ref={thumbOverlayRef} $visible={isThumbZoneOverlayVisible}>
            <$ThumbLayerRed />
            <$ThumbLayerOrange />
            <$ThumbLayerGreen />
          </$ThumbZoneOverlay>,
          document.body,
        )}
      <InventoryMobileQrModal
        isOpen={isMobileQrModalOpen}
        mobileUrl={inventoryMobileOpenUrl}
        onClose={() => setIsMobileQrModalOpen(false)}
      />
      {typeof document !== 'undefined' &&
        isScanCameraOpen &&
        createPortal(
          <$ScanBackdrop ref={scanBackdropRef} onClick={handleCloseScanCamera}>
            <$ScanPanel onClick={(event) => event.stopPropagation()}>
              <$ScanTitle id="inventory-scan-title">
                {isResponsiveScanIncrementActive
                  ? 'Scan inventaire — +1 par code'
                  : 'Scanner un code-barres'}
              </$ScanTitle>
              {scanCameraError ? <$ScanMessage $variant="error">{scanCameraError}</$ScanMessage> : null}
              {scanCameraInfo ? <$ScanMessage $variant="info">{scanCameraInfo}</$ScanMessage> : null}
              <$ScanVideo ref={scanVideoRef} autoPlay muted playsInline />
              <$ScanActions>
                <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleCloseScanCamera}>
                  Fermer
                </Button>
              </$ScanActions>
            </$ScanPanel>
          </$ScanBackdrop>,
          document.body,
        )}
    </$Page>
  );
};

export default InventoryDetailPage;
